import type { Item } from './types';

export type QuantidadesGrupos = Record<string, number | null>;
export const chaveGrupo = (servicoId?: string | null) => servicoId || '__sem_servico__';
const arredondar = (valor: number) => Math.sign(valor)
  * Math.round((Math.abs(valor) + Number.EPSILON * Math.max(1, Math.abs(valor))) * 100) / 100;

export function quantidadeGrupoValida(valor: number | null): boolean {
  return valor !== null && Number.isFinite(valor) && valor > 0 && valor <= 1000000
    && Math.abs(valor * 10000 - Math.round(valor * 10000)) < 0.000001;
}

// Itens descrevem UMA unidade do serviço; só o subtotal multiplica o grupo.
// Arredondar a venda unitária antes da multiplicação mantém a proposta idêntica.
export function calcularComposicao(itens: Item[], quantidades: QuantidadesGrupos = {}) {
  const grupos = new Map<string, { custoUnitario: number; vendaUnitaria: number }>();
  itens.forEach(it => {
    const chave = chaveGrupo(it.servico_id);
    const grupo = grupos.get(chave) || { custoUnitario: 0, vendaUnitaria: 0 };
    const custo = it.quantidade * (it.coeficiente ?? 1) * it.valor_unitario;
    grupo.custoUnitario += custo;
    grupo.vendaUnitaria += custo * (1 + (it.markup_pct || 0) / 100);
    grupos.set(chave, grupo);
  });
  const totaisGrupos = new Map(Array.from(grupos, ([chave, grupo]) => {
    const quantidade = quantidades[chave] === undefined ? 1 : quantidades[chave];
    const multiplicador = quantidade !== null && quantidadeGrupoValida(quantidade) ? quantidade : 0;
    const vendaUnitaria = arredondar(grupo.vendaUnitaria);
    return [chave, {
      ...grupo, vendaUnitaria, quantidade,
      totalCusto: arredondar(grupo.custoUnitario * multiplicador),
      totalVenda: arredondar(vendaUnitaria * multiplicador),
    }];
  }));
  return {
    grupos: totaisGrupos,
    totalCusto: arredondar(Array.from(totaisGrupos.values()).reduce((s, g) => s + g.totalCusto, 0)),
    totalVenda: arredondar(Array.from(totaisGrupos.values()).reduce((s, g) => s + g.totalVenda, 0)),
  };
}

// Mesclar grupos com quantidades diferentes mudaria o orçamento silenciosamente.
export function trocarQuantidadeGrupo(quantidades: QuantidadesGrupos, origem: string,
  destino: string, destinoExiste: boolean): QuantidadesGrupos | null {
  const quantidadeOrigem = quantidades[origem] === undefined ? 1 : quantidades[origem];
  const quantidadeDestino = quantidades[destino] === undefined ? 1 : quantidades[destino];
  if (destinoExiste && quantidadeOrigem !== quantidadeDestino) return null;
  const novas = { ...quantidades, [destino]: quantidadeOrigem };
  delete novas[origem];
  return novas;
}
